import React, { useState } from 'react';
import './style.less';

const DEPLOY_MODES = [
  {
    id: 'k3s',
    title: '单机 All-in-One 敏捷版',
    subtitle: '适合 PoC 概念验证、部门级敏捷团队、单台服务器快速交付',
    cli: './install --target k3s --tls selfsigned',
    featured: false,
    features: [
      '离线安装包内置 k3s 运行时与内置 Harbor 镜像仓库，零外网依赖',
      '支持自签名证书（selfsigned）、自有证书（cert）或前置代理（proxy）三种 HTTPS 模式',
      '自动导入官方工作间镜像、节点预热、导入签名插件包并创建默认项目共享模板',
      '管理员首次登录完成改密与 MFA 绑定后即可直接创建并打开工作间',
    ],
    ctaText: '登录控制台体验',
  },
  {
    id: 'kubernetes',
    badge: '★ 推荐生产架构',
    title: '企业 Kubernetes 集群版',
    subtitle: '适合已具备自建 kubeadm / RKE2 或云厂商 ACK / TKE 集群的企业',
    cli: './install --target kubernetes --expose ingress',
    featured: true,
    features: [
      '非侵入式部署：不改动现有节点内核与 containerd 配置，运维机远程一键执行',
      '全面复用企业既有 Harbor 仓库、默认 StorageClass 与 Ingress / LoadBalancer',
      '修改集群前自动完成管理员权限、NetworkPolicy、架构与镜像拉取能力预检',
      '支持在线平滑更换访问域名与 SSH 网关暴露地址（geekcode-deploy access set）',
    ],
    ctaText: '立即登录控制台',
  },
  {
    id: 'multi-cluster',
    title: '多集群控制与算力分离版',
    subtitle: '适合大型集团多部门隔离、跨机房地域调度、高并发生产扩容',
    cli: './install --role control && geekcode-deploy cluster attach',
    featured: false,
    features: [
      '控制面独立承载 API、控制台与防篡改审计库，不运行任何工作间负载',
      '算力层支持按需接入多个独立 K8s 工作集群，通过节点污点实现专机专用',
      '构建扫描命名空间（geekcode-build）与工作间命名空间严格执行网络隔离',
      '支持按项目绑定指定算力集群，内置节点磁盘水位避让与跨集群实时监控',
    ],
    ctaText: '登录控制台体验',
  },
];

const BOOTSTRAP_STEPS = [
  { step: '01', name: '签名与环境预检', desc: 'Ed25519 验签 · 端口与 StorageClass 检查' },
  { step: '02', name: '仓库与构建命名空间', desc: 'Harbor 就绪 · 启用 geekcode-build 守护' },
  { step: '03', name: '镜像合规扫描与发布', desc: '16 项规范校验 · 离线漏洞库扫描门禁' },
  { step: '04', name: '节点 DaemonSet 预热', desc: '工作间节点预拉取镜像，首次创建秒开' },
  { step: '05', name: '离线插件验签上架', desc: '导入 official.gcpb 与 OSV 离线漏洞库' },
  { step: '06', name: '默认项目与模板就绪', desc: '配置白名单 · 生成 VS Code / Java / Jupyter 模板' },
];

const copyText = async (text) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

const PricingSection = () => {
  const [copiedId, setCopiedId] = useState(null);

  const handleCopy = async (id, cli) => {
    try {
      await copyText(cli);
      setCopiedId(id);
      setTimeout(() => {
        setCopiedId((prev) => (prev === id ? null : prev));
      }, 2000);
    } catch (_) {
      setCopiedId(null);
    }
  };

  return (
    <div className="deployment-section-wrap">
      <div className="deployment-grid">
        {DEPLOY_MODES.map((mode) => (
          <article
            key={mode.id}
            className={`deployment-card cloud-collab-card ${mode.featured ? 'featured' : ''}`}
          >
            {mode.badge && <div className="recommend-badge">{mode.badge}</div>}

            <h3>{mode.title}</h3>
            <p className="mode-subtitle">{mode.subtitle}</p>

            <div className="cli-box">
              <code title={mode.cli}>{mode.cli}</code>
              <button
                type="button"
                className={`copy-btn ${copiedId === mode.id ? 'copied' : ''}`}
                onClick={() => handleCopy(mode.id, mode.cli)}
                aria-label={`复制 ${mode.title} 部署命令`}
              >
                {copiedId === mode.id ? '✓ 已复制' : '复制'}
              </button>
            </div>

            <ul className="mode-features">
              {mode.features.map((feat) => (
                <li key={feat}>
                  <span className="bullet-icon" aria-hidden="true">✓</span>
                  <span>{feat}</span>
                </li>
              ))}
            </ul>

            <a
              className={`mode-cta-btn ${mode.featured ? 'primary' : 'secondary'}`}
              href="https://console.geekcode.cloud/login/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>{mode.ctaText}</span>
              <span aria-hidden="true">↗</span>
            </a>
          </article>
        ))}
      </div>

      <div className="bootstrap-pipeline cloud-collab-card">
        <div className="pipeline-header">
          <div>
            <span className="pipeline-badge">装完即用 · ZERO-TOUCH BOOTSTRAP</span>
            <h4>6 步幂等自动化初始化流水线（支持中断续跑与实时状态观测）</h4>
          </div>
          <code className="status-cmd">geekcode-deploy bootstrap status</code>
        </div>

        <div className="pipeline-steps">
          {BOOTSTRAP_STEPS.map((item) => (
            <div key={item.step} className="pipeline-step-card">
              <div className="step-num">STEP {item.step}</div>
              <div className="step-name">{item.name}</div>
              <div className="step-desc">{item.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PricingSection;
